import React, { useEffect } from "react";
import fp from "lodash/fp";
import { Col, Row, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Field, unregisterField, change } from "redux-form";

import { RequiredMark, Skeleton, SelectFormGroup } from "components";
import { CustomDropdownByValue } from "components/formFields/dropdown";
import { DebounceTimeout, RESIDUAL_SECTION_NAME } from "globalConstants";
import { dispatchChangeScheduleAFirstAsDefault } from "helpers";
import { useLoader } from "hooks";
import { filterScheduleAsAccordingProcessor } from "mappers";
import { dropdownValuesActions } from "store";
import {
	getCurrentUserId,
	getCurrentUserIsAgent,
} from "store/user/user.reducer";
import { noActiveAgents } from "validators/applicationValidators";
import { applyValidatorsIf, required } from "validators/validation";

const PrimaryAgentsSection = ({
	formName,
	primaryAgents,
	primaryAgentLoading,
	headquartersValue, // Required as select checks props, not <input /> for values
	headquartersLoading,
	formValues,
	handleGetScheduleAList,
	setRawScheduleAs,
	setFilteredScheduleAs,
	initialValues,
}) => {
	const dispatch = useDispatch();

	const currentUserId = useSelector(getCurrentUserId);
	const isAgent = useSelector(getCurrentUserIsAgent);

	/**
	 * Preselect Primary Agent DDL if the current user role is an agent
	 */

	const initialPrimaryAgentId = initialValues?.primaryAgentId;

	useEffect(() => {
		// Load scheduleA list for the preselected primary agent
		const setInitialValueForScheduleAList = async primaryAgentId => {
			const scheduleAListData = await handleGetScheduleAList(
				primaryAgentId,
			);
			setRawScheduleAs(scheduleAListData);
		};

		// Disable preselect for edit page
		if (!initialPrimaryAgentId) {
			if (Array.isArray(primaryAgents) && currentUserId) {
				const preselectedPrimaryAgent = fp.find(
					agent => agent.value === currentUserId,
					primaryAgents,
				);

				if (isAgent && preselectedPrimaryAgent) {
					dispatch(
						change(
							formName,
							"primaryAgentId",
							preselectedPrimaryAgent?.value,
						),
					);
					// Set full primary agent data into form filed to show on App Summary page
					dispatch(
						change(
							formName,
							"primaryAgent",
							preselectedPrimaryAgent,
						),
					);

					setInitialValueForScheduleAList(
						preselectedPrimaryAgent.value,
					);
				}
			}
		}
	}, [
		currentUserId,
		dispatch,
		formName,
		initialPrimaryAgentId,
		isAgent,
		primaryAgents,
		setRawScheduleAs,
		handleGetScheduleAList,
	]);

	useEffect(() => {
		dispatch(unregisterField(formName, "noData"));
	}, [dispatch, formName, primaryAgentLoading]);

	const headquarters =
		headquartersValue && headquartersValue.id
			? {
					value: headquartersValue.id,
					label: headquartersValue.name,
			  }
			: null;

	const handleLoadOptions = (pattern, prevOptions, { page }) =>
		dispatch(
			dropdownValuesActions.loadHeadquartersMerchants({
				pattern,
				prevOptions,
				page,
			}),
		);

	const handleHeadquartersChange = option => {
		dispatch(
			change(formName, "headquarters", {
				id: option?.value ?? "",
				name: option?.label ?? "",
			}),
		);
	};

	const handlePrimaryAgentChange = async e => {
		dispatch(change(formName, `${RESIDUAL_SECTION_NAME}.scheduleA.id`, ""));
		dispatch(
			change(formName, `${RESIDUAL_SECTION_NAME}.splitProfile.id`, ""),
		);

		const selectedAgent = primaryAgents?.find(
			agent => agent.value === e?.target?.value,
		);

		dispatch(change(formName, "primaryAgent", selectedAgent));

		const primaryAgentId = e?.target?.value;
		const processorId = formValues?.processorSponsorSection?.processor?.id;

		let scheduleAListData = [];

		if (primaryAgentId) {
			scheduleAListData = await handleGetScheduleAList(primaryAgentId);
			setRawScheduleAs(scheduleAListData);
		}

		if (primaryAgentId && processorId) {
			const filteredScheduleAsAccordingProcessor =
				filterScheduleAsAccordingProcessor?.(
					scheduleAListData,
					processorId,
				);
			setFilteredScheduleAs(filteredScheduleAsAccordingProcessor);
			dispatchChangeScheduleAFirstAsDefault(
				dispatch,
				formName,
				filteredScheduleAsAccordingProcessor,
			);
		} else {
			setFilteredScheduleAs([]);
			dispatch(
				change(formName, `${RESIDUAL_SECTION_NAME}.scheduleA.id`, ""),
			);
		}
	};

	const Loader = useLoader(primaryAgentLoading);

	return (
		<Row>
			<Col md={5}>
				<Loader
					skeleton={
						<Row>
							<Col sm={6} style={{ display: "contents" }}>
								<Form.Label column sm={6}>
									Primary Agent
									<RequiredMark />
								</Form.Label>
							</Col>
							<Col sm={6}>
								<Skeleton height="38px" />
							</Col>
						</Row>
					}
					component={
						<Field
							component={CustomDropdownByValue}
							label="Primary Agent"
							validate={applyValidatorsIf(
								() => primaryAgents?.length > 0,
							)([required], [noActiveAgents])}
							name="primaryAgentId"
							loading={primaryAgentLoading}
							items={primaryAgents}
							onChange={handlePrimaryAgentChange}
						/>
					}
				/>
			</Col>
			<Col md={{ span: 5, offset: 2 }}>
				<Field
					loadOptions={handleLoadOptions}
					onChangeCb={handleHeadquartersChange}
					valueAlias={headquarters}
					component={SelectFormGroup}
					async
					debounceTimeout={DebounceTimeout}
					name="headquarters"
					blurLoading={headquartersLoading}
					label="Headquarters"
					isClearable
					hideRequiredMark
					defaultOptions
				/>
			</Col>
		</Row>
	);
};

export default PrimaryAgentsSection;
